import SbEditable from 'storyblok-react';
import { css, type Theme } from '@emotion/react';

import { makeStyles, useTheme } from '@toolbox/style';
import {
  Box,
  Grid,
  GridItem,
  gridSizeList,
  Heading,
  Text,
  Typography,
  InfoIconWithTooltip,
  Link,
} from '@toolbox/components';
import { isEmpty } from '@toolbox/utils';
import {
  NumberInputField,
  QuestionLayout,
  ZipCodeField,
  TextInputField,
  PhoneNumberField,
  SwitchField,
  HiddenField,
  CheckboxField,
  RadioGroupField,
  RadioCard,
  ActionButton,
  CloseButton,
  SelectField,
  AddressSuggestField,
  CardComponent,
  FeedbackForm,
  IconWithText,
  OrderedList,
  RegionalTrend,
  RegionalTrendSection,
} from '../components';
import { stringOrUndefined } from './utils';

import type {
  SelectSchema,
  ImageSchema,
  QuestionLayoutSchema,
  GridSchema,
  GridItemSchema,
  ZipCodeFieldSchema,
  AddressSuggestFieldSchema,
  NumberInputFieldSchema,
  HeadingSchema,
  TextSchema,
  TextInputFieldSchema,
  PhoneNumberFieldSchema,
  EmailFieldSchema,
  CheckBoxFieldSchema,
  BoxSchema,
  OptionSchema,
  OptionGroupSchema,
  ActionButtonSchema,
  CloseButtonSchema,
  DisclaimerSchema,
  SwitchFieldSchema,
  HiddenFieldSchema,
  LinkSchema,
  CardSchema,
  FeedbackFormSchema,
  IconWithTextSchema,
  OrderedListSchema,
  RegionalTrendSchema,
  RegionalTrendSectionSchema,
} from './schemas';
import type { z } from 'zod';
import type { ReactNode } from 'react';
import type { BreakpointUnit } from '@toolbox/style';
import type { GridSize, BreakPointOrderProp } from '@toolbox/components';
import type { ReactFCWithChildren } from '@widget/types/ReactFCWithChildren';

type QuestionLayoutProps = z.infer<typeof QuestionLayoutSchema>;
type GridProps = z.infer<typeof GridSchema>;
type GridItemProps = z.infer<typeof GridItemSchema>;
type ImageProps = z.infer<typeof ImageSchema>;
type ZipCodeFieldProps = z.infer<typeof ZipCodeFieldSchema>;
type AddressSuggestFieldProps = z.infer<typeof AddressSuggestFieldSchema>;
type NumberInputFieldProps = z.infer<typeof NumberInputFieldSchema>;
type HeadingProps = z.infer<typeof HeadingSchema>;
type TextProps = z.infer<typeof TextSchema>;
type TextInputFieldProps = z.infer<typeof TextInputFieldSchema>;
type PhoneNumberFieldProps = z.infer<typeof PhoneNumberFieldSchema>;
type SwitchFieldProps = z.infer<typeof SwitchFieldSchema>;
type HiddenFieldProps = z.infer<typeof HiddenFieldSchema>;
type EmailFieldProps = z.infer<typeof EmailFieldSchema>;
type CheckBoxFieldProps = z.infer<typeof CheckBoxFieldSchema>;
type BoxProps = z.infer<typeof BoxSchema>;
type OptionProps = z.infer<typeof OptionSchema>;
type OptionGroupProps = z.infer<typeof OptionGroupSchema>;
type ActionButtonProps = z.infer<typeof ActionButtonSchema>;
type CloseButtonProps = z.infer<typeof CloseButtonSchema>;
type DisclaimerProps = z.infer<typeof DisclaimerSchema>;
type LinkProps = z.infer<typeof LinkSchema>;
type SelectProps = z.infer<typeof SelectSchema>;
type CardProps = z.infer<typeof CardSchema>;
type IconWithTextProps = z.infer<typeof IconWithTextSchema>;
type OrderedListProps = z.infer<typeof OrderedListSchema>;
type FeedbackFormProps = z.infer<typeof FeedbackFormSchema>;
type RegionalTrendProps = z.infer<typeof RegionalTrendSchema>;
type RegionalTrendSectionProps = z.infer<typeof RegionalTrendSectionSchema>;

type Props =
  | QuestionLayoutProps
  | GridProps
  | GridItemProps
  | ImageProps
  | ZipCodeFieldProps
  | AddressSuggestFieldProps
  | NumberInputFieldProps
  | HeadingProps
  | TextProps
  | TextInputFieldProps
  | PhoneNumberFieldProps
  | SwitchFieldProps
  | HiddenFieldProps
  | EmailFieldProps
  | CheckBoxFieldProps
  | ActionButtonProps
  | DisclaimerProps
  | BoxProps
  | OptionProps
  | OptionGroupProps
  | LinkProps
  | SelectProps
  | CardProps
  | IconWithTextProps
  | OrderedListProps
  | FeedbackFormProps
  | RegionalTrendProps
  | RegionalTrendSectionProps;

function toGridSize(gridItemSize: string): GridSize | undefined {
  if (!gridItemSize) {
    return undefined;
  }

  const gridSize = +gridItemSize as GridSize;
  return gridSizeList.includes(gridSize) ? gridSize : undefined;
}

function getBreakpoints(
  gridItemProps: GridItemProps,
): Record<BreakpointUnit, GridSize | undefined> {
  return {
    xs: toGridSize(gridItemProps.xs),
    sm: toGridSize(gridItemProps.sm),
    md: toGridSize(gridItemProps.md),
    lg: toGridSize(gridItemProps.lg),
    xl: toGridSize(gridItemProps.xl),
  };
}

function getOrderBreakpoints(
  gridItemProps: GridItemProps,
): Record<BreakPointOrderProp, string | null | undefined> {
  return {
    orderxs: gridItemProps.orderXs,
    ordersm: gridItemProps.orderSm,
    ordermd: gridItemProps.orderMd,
    orderlg: gridItemProps.orderLg,
    orderxl: gridItemProps.orderXl,
  };
}

const useStyles = makeStyles(({ theme }) => ({
  actionBtn: css`
    &.large {
      line-height: ${theme.lineHeights.md};
    }
  `,
  actionBtnSubText: css`
    font-size: ${theme.fontSizes.f3};
    font-weight: ${theme.fontWeights.normal};
  `,
  disclaimer: css`
    color: ${theme.palette.colors.gray['400']};
    margin-top: ${theme.spacing(4)};

    a {
      text-decoration: underline;
      color: inherit;
    }
  `,
}));

const BoxComponent: React.FC<BoxProps> = (props: BoxProps) => {
  const theme = useTheme();

  if (!props.children?.length) {
    return null;
  }

  return (
    <Box
      key={props._uid}
      mb={!props.mb ? undefined : (theme.spacings[props.mb] as keyof Theme['spacings'])}
      mt={!props.mt ? undefined : (theme.spacings[props.mt] as keyof Theme['spacings'])}
      ml={!props.ml ? undefined : (theme.spacings[props.ml] as keyof Theme['spacings'])}
      mr={!props.mr ? undefined : (theme.spacings[props.mr] as keyof Theme['spacings'])}
      textAlign={!props.textAlign ? undefined : props.textAlign}
      maxWidth={!props.maxWidth ? undefined : props.maxWidth}
    >
      {props.children.map(renderComponent)}
    </Box>
  );
};

const ActionButtonComponent: ReactFCWithChildren<ActionButtonProps> = (
  props: ActionButtonProps,
) => {
  const classes = useStyles();
  return (
    <ActionButton
      css={classes.actionBtn as any}
      key={props._uid}
      submitTarget={props.submitTarget}
      fullWidth
      size="large"
      submit
      submitWidget
    >
      {props.text}
      {props.subText && <div css={classes.actionBtnSubText as any}>{props.subText}</div>}
    </ActionButton>
  );
};

const CloseButtonComponent: ReactFCWithChildren<CloseButtonProps> = (props: CloseButtonProps) => {
  const classes = useStyles();

  return (
    <CloseButton css={classes.actionBtn as any} key={props._uid} size="medium">
      {props.text}
    </CloseButton>
  );
};

const OptionComponent: ReactFCWithChildren<OptionProps> = (props: OptionProps) => {
  const theme = useTheme();

  const imgCss = css`
    width: 1em;
    height: 1em;
    margin-right: ${theme.spacing(4)};
    font-size: ${theme.iconSizes.sm}px;

    ${theme.media.up('sm')} {
      font-size: ${theme.iconSizes.md}px;
    }
  `;

  const { image, icon, ...otherProps } = props;

  const iconItem =
    typeof image === 'object' && image?.filename ? (
      <img css={imgCss as any} src={image?.filename} alt={image?.alt || ''} />
    ) : (
      icon
    );

  return <RadioCard key={props._uid} {...otherProps} icon={iconItem} />;
};

const DisclaimerComponent: ReactFCWithChildren<DisclaimerProps> = (props: DisclaimerProps) => {
  const classes = useStyles();

  return (
    <Typography
      css={classes.disclaimer as any}
      textAlign={props.textAlign || 'left'}
      color={props.color || undefined}
      fontFamily={props.fontFamily || undefined}
      fontSize={props.fontSize || undefined}
      fontWeight={props.fontWeight || undefined}
      lineHeight={props.lineHeight || undefined}
      display={props.display || 'inline'}
      key={props._uid}
      variant="c2"
      dangerouslySetInnerHTML={{ __html: props.text ?? '' }}
    />
  );
};

const components = {
  QuestionLayout: (props: QuestionLayoutProps) => (
    <QuestionLayout
      key={props._uid}
      back={props.back}
      submit={props.submit}
      skip={props.skip}
      title={props.title}
      description={props.description}
      backButtonText={stringOrUndefined(props.backButtonText)}
      closeButtonText={stringOrUndefined(props.closeButtonText)}
      submitButtonText={stringOrUndefined(props.submitButtonText)}
      skipButtonText={stringOrUndefined(props.skipButtonText)}
      maxW={!props.maxW ? undefined : props.maxW}
    >
      {props.children?.map(renderComponent)}
    </QuestionLayout>
  ),
  Image: (props: ImageProps) => {
    if (props.asset === '') {
      return null;
    }
    return (
      <img
        key={props._uid}
        src={props.asset.filename}
        alt={props.asset.alt || ''}
        style={{
          maxWidth: props.maxWidth || undefined,
          maxHeight: props.maxHeight || undefined,
          width: props.width || undefined,
          height: props.height || undefined,
        }}
      />
    );
  },
  Grid: (props: GridProps) => (
    <Grid key={props._uid} alignItems={props.align_items} justifyContent={props.justify_content}>
      {props.children?.map(renderComponent)}
    </Grid>
  ),
  GridItem: (props: GridItemProps) => (
    <GridItem
      key={props._uid}
      {...getBreakpoints(props)}
      {...getOrderBreakpoints(props)}
      hideUnderBreakpoint={props.hideUnderBreakpoint}
      customStyle={props.customStyle}
    >
      {props.children?.map(renderComponent)}
    </GridItem>
  ),
  Link: (props: LinkProps) => (
    <Link
      key={props._uid}
      href={props.url || ''}
      target={props.openInNewWindow ? 'blank' : '_parent'}
      textAlign={props.textAlign || 'left'}
      color={props.color || undefined}
      fontFamily={props.fontFamily || undefined}
      fontSize={props.fontSize || undefined}
      fontWeight={props.fontWeight || undefined}
      variant={props.variant || undefined}
    >
      {props.text}
    </Link>
  ),
  ZipCodeField: (props: ZipCodeFieldProps) => (
    <ZipCodeField
      key={props._uid}
      keepErrorSpace={props.keepErrorSpace}
      label={props.label}
      name={!props.name ? 'placeholder' : props.name}
      coverageFieldName={props.coverageFieldName}
    />
  ),
  // @todo do we still need MapBox AddressSuggestField ? or we can delete it
  AddressSuggestField: (props: AddressSuggestFieldProps) => (
    <AddressSuggestField
      key={props._uid}
      label={props.label}
      name={props.name}
      required={props.required}
      cityFieldName={props.cityFieldName}
      streetFieldName={props.streetFieldName}
      zipCodeFieldName={props.zipCodeFieldName}
      noHouseNumberError={props.noHouseNumberError}
      requiredErrorText={props.requiredErrorText}
    />
  ),
  NumberInputField: (props: NumberInputFieldProps) => (
    <NumberInputField
      key={props._uid}
      required={props.required}
      label={props.label}
      showTooltip={props.showTooltip || false}
      tooltip={props.tooltip || ''}
      name={!props.name ? 'placeholder' : props.name}
      min={isEmpty(props.min) ? undefined : parseInt(props.min, 10)}
      max={isEmpty(props.max) ? undefined : parseInt(props.max, 10)}
      helperButtonStep={isEmpty(props.helperButtonStep) ? 1 : parseInt(props.helperButtonStep, 10)}
      useHelperButtons={props.useHelperButtons || false}
      decimalPrecision={
        isEmpty(props.decimalPrecision) ? undefined : parseInt(props.decimalPrecision, 10)
      }
      suffix={props.suffix}
    />
  ),
  Heading: (props: HeadingProps) => (
    <Heading key={props._uid} variant={props.variant === '' ? 'h1' : props.variant}>
      {props.text}
    </Heading>
  ),
  Text: (props: TextProps) => (
    <Text
      key={props._uid}
      textAlign={props.textAlign || 'left'}
      color={props.color || undefined}
      fontFamily={props.fontFamily || undefined}
      fontSize={props.fontSize || undefined}
      fontWeight={props.fontWeight || undefined}
      lineHeight={props.lineHeight || undefined}
      display={props.display || 'inline'}
      variant={props.variant ? props.variant : 'b1'}
    >
      {props.text}
    </Text>
  ),
  TextInputField: (props: TextInputFieldProps) => (
    <TextInputField
      key={props._uid}
      showTooltip={props.showTooltip || false}
      tooltip={props.tooltip || ''}
      required={props.required}
      label={props.label}
      defaultValue={props.defaultValue}
      name={!props.name ? 'placeholder' : props.name}
    />
  ),
  EmailField: (props: EmailFieldProps) => (
    <TextInputField
      key={props._uid}
      required={props.required}
      rules={{
        email: true,
      }}
      tooltipPlacement={'top-end'}
      aria-describedby={props._uid}
      tooltipIcon="EsclamationIcon"
      type="email"
      tooltip={props.tooltip || ''}
      showTooltip={props.showTooltip || false}
      suffix={
        props.infoText && (
          <InfoIconWithTooltip
            id={props._uid}
            placement="left"
            arrowCornerPadding={3}
            text={props.infoText}
          />
        )
      }
      name={props.name || 'placeholder'}
      label={props.label}
    />
  ),
  PhoneNumberField: (props: PhoneNumberFieldProps) => (
    <PhoneNumberField
      key={props._uid}
      required={props.required}
      aria-describedby={props._uid}
      name={props.name || 'placeholder'}
      label={props.label}
      tooltip={props.tooltip || ''}
      showTooltip={props.showTooltip || false}
      tooltipPlacement={'top-end'}
      tooltipIcon="EsclamationIcon"
      suffix={
        props.infoText && (
          <InfoIconWithTooltip
            id={props._uid}
            placement="left"
            arrowCornerPadding={3}
            text={props.infoText}
          />
        )
      }
      disabled={props.disabled || false}
      additionalValidation={props.additionalValidation || false}
    />
  ),
  SwitchField: (props: SwitchFieldProps) => (
    <SwitchField
      key={props._uid}
      options={(props.options || '').split(',')}
      width={props.width || '200px'}
      aria-describedby={props._uid}
      name={props.name || 'placeholder'}
      required
      defaultValue={props.defaultValue || ''}
      errorMessage={props.errorMessage || ''}
      variant={props.variant}
      keepErrorSpace={props.keepErrorSpace}
    />
  ),
  HiddenField: (props: HiddenFieldProps) => (
    <HiddenField
      key={props._uid}
      type={props.type}
      name={props.name || 'placeholder'}
      storyBlokValue={props.value || ''}
    />
  ),
  CheckBoxField: (props: CheckBoxFieldProps) => (
    <CheckboxField
      key={props._uid}
      required={props.required}
      label={props.label}
      name={!props.name ? 'placeholder' : props.name}
    />
  ),
  Box: (props: BoxProps) => <BoxComponent key={props._uid} {...props} />,
  Option: (props: OptionProps) => <OptionComponent key={props._uid} {...props} />,
  OptionGroup: (props: OptionGroupProps) => (
    <RadioGroupField key={props._uid} name={props.name} submit={props.submit}>
      {props.children?.map(renderComponent)}
    </RadioGroupField>
  ),
  ActionButton: (props: ActionButtonProps) => <ActionButtonComponent key={props._uid} {...props} />,
  CloseButton: (props: CloseButtonProps) => <CloseButtonComponent key={props._uid} {...props} />,
  Disclaimer: (props: DisclaimerProps) => <DisclaimerComponent key={props._uid} {...props} />,
  Select: (props: SelectProps) => <SelectField key={props._uid} {...props} />,
  Card: (props: CardProps) => <CardComponent key={props._uid} {...props} />,
  FeedbackForm: (props: FeedbackFormProps) => <FeedbackForm key={props._uid} {...props} />,
  IconWithText: (props: IconWithTextProps) => (
    <IconWithText key={props._uid} iconType={props.iconType || undefined} text={props.text} />
  ),
  OrderedList: (props: OrderedListProps) => (
    <OrderedList key={props._uid} items={props.items} title={props.title} />
  ),
  RegionalTrend: (props: RegionalTrendProps) => <RegionalTrend key={props._uid} {...props} />,
  RegionalTrendSection: (props: RegionalTrendSectionProps) => (
    <RegionalTrendSection key={props._uid}>
      {props.children?.map(renderComponent)}
    </RegionalTrendSection>
  ),
};

export function renderComponent(props: Props): ReactNode {
  const component = components[props.component]?.(props as never as any);

  if (!component) {
    return <></>;
  }

  return (
    <SbEditable content={props} key={props._uid}>
      {component}
    </SbEditable>
  );
}
